import { useState } from "react";
import { toast } from "sonner";
import { Download, History, Loader2, RotateCcw, Trash2, Upload } from "lucide-react";
import { api, downloadDoc, errMsg } from "@/lib/api";
import { fmtDate, fmtSize } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const NewVersion = ({ doc, onSaved }) => {
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [inputKey, setInputKey] = useState(0);
  const upload = async () => {
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("notes", notes);
    try {
      const { data } = await api.post(`/documents/${doc.id}/versions`, fd);
      toast.success(`Uploaded v${data.current_version}`);
      setFile(null);
      setNotes("");
      setInputKey((k) => k + 1);
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-xl border border-dashed border-primary/50 p-4" data-testid="new-version-box">
      <Label className="font-bold">Upload a new version</Label>
      <Input key={inputKey} type="file" onChange={(e) => setFile(e.target.files[0])} data-testid="new-version-file-input" />
      <Input placeholder="What changed? (version notes)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="new-version-notes-input" />
      <button onClick={upload} disabled={busy} data-testid="new-version-upload-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload v{doc.current_version + 1}
      </button>
    </div>
  );
};

export const DocumentDialog = ({ doc, onClose, onChange, onDelete, isAdmin }) => {
  const dl = (v) => downloadDoc(doc.id, v.version, v.filename).catch((e) => toast.error(errMsg(e)));
  const restore = async (v) => {
    try {
      const { data } = await api.post(`/documents/${doc.id}/restore/${v.version}`);
      toast.success(`v${v.version} restored as v${data.current_version}`);
      onChange(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${doc.name}"? Version history is retained in the archive.`)) return;
    await api.delete(`/documents/${doc.id}`);
    toast.success("Document deleted");
    onDelete(doc);
  };
  const versions = [...doc.versions].reverse();

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="document-dialog">
        <DialogHeader><DialogTitle className="pr-6 font-display text-2xl">{doc.name}</DialogTitle></DialogHeader>
        {doc.description && <p className="text-sm text-muted-foreground">{doc.description}</p>}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => dl(doc.versions[doc.versions.length - 1])} data-testid="doc-download-latest-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground"><Download className="h-4 w-4" /> Download v{doc.current_version}</button>
          {isAdmin && <button onClick={remove} data-testid="doc-delete-btn" className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-4 py-2 text-sm font-semibold text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /> Delete</button>}
        </div>
        {isAdmin && <NewVersion doc={doc} onSaved={onChange} />}
        <div>
          <h4 className="mb-2 flex items-center gap-2 font-bold"><History className="h-4 w-4 text-primary" /> Version history</h4>
          <ol className="relative space-y-3 border-l border-border pl-5" data-testid="version-history">
            {versions.map((v) => (
              <li key={v.version} data-testid={`version-${v.version}`} className="relative">
                <span className={`absolute -left-[26px] top-1.5 h-3 w-3 rounded-full border-2 ${v.version === doc.current_version ? "border-primary bg-primary" : "border-zinc-500 bg-background"}`} />
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold"><span className="font-mono text-primary">v{v.version}</span> · <span className="break-all">{v.filename}</span> {v.version === doc.current_version && <span className="ml-1 rounded bg-primary/15 px-1.5 text-[10px] font-bold uppercase text-primary">Current</span>}</div>
                    <div className="text-xs text-muted-foreground">{v.uploaded_by} · {fmtDate(v.uploaded_at)} · {fmtSize(v.size)}</div>
                    {v.notes && <div className="mt-0.5 text-xs italic text-muted-foreground">{v.notes}</div>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => dl(v)} data-testid={`version-download-${v.version}`} className="rounded-full p-2 text-muted-foreground hover:bg-secondary hover:text-foreground" title="Download"><Download className="h-4 w-4" /></button>
                    {isAdmin && v.version !== doc.current_version && <button onClick={() => restore(v)} data-testid={`version-restore-${v.version}`} className="rounded-full p-2 text-muted-foreground hover:bg-secondary hover:text-foreground" title="Restore as current"><RotateCcw className="h-4 w-4" /></button>}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </DialogContent>
    </Dialog>
  );
};
